import { getMonthsElapsed, isActiveInMonth } from './dates.js';
import type {
  AggregateCalculationResult,
  CalculationResult,
  StandardizedCustomerData,
  StandardizedExpense,
  StandardizedRevenue,
} from './types.js';

export class FinancialCalculationService {
  static calculateMonthsElapsed(startDate: Date | undefined, targetDate: Date): number {
    if (!startDate) return 0;
    return getMonthsElapsed(startDate, targetDate);
  }

  static normalizeToMonthly(amount: number, frequency: string = 'monthly'): number {
    switch (frequency?.toLowerCase()) {
      case 'yearly':
      case 'annual':
        return amount / 12;
      case 'quarterly':
        return amount / 3;
      case 'one-time':
        return 0;
      case 'monthly':
      default:
        return amount;
    }
  }

  static applyGrowthRate(baseAmount: number, monthsElapsed: number, growthRate: number = 0): number {
    if (growthRate === 0 || monthsElapsed <= 0) return baseAmount;
    return baseAmount * Math.pow(1 + growthRate, monthsElapsed);
  }

  static calculateExpenseAtMonth(
    expense: StandardizedExpense,
    targetDate: Date,
    totalMonthlyRevenue: number,
    customerData?: StandardizedCustomerData
  ): CalculationResult {
    if (!isActiveInMonth(expense, targetDate)) {
      return {
        calculatedAmount: 0,
        breakdown: { baseAmount: 0, revenueComponent: 0, customerComponent: 0, growthMultiplier: 1 },
      };
    }

    const monthsElapsed = this.calculateMonthsElapsed(expense.startDate, targetDate);
    const monthlyAmount = this.normalizeToMonthly(expense.amount, expense.frequency);
    const monthlyBaseAmount = this.normalizeToMonthly(
      expense.customerBaseAmount ?? 0,
      expense.frequency
    );

    const result: CalculationResult = {
      calculatedAmount: 0,
      breakdown: {
        baseAmount: 0,
        revenueComponent: 0,
        customerComponent: 0,
        growthMultiplier: 1,
      },
    };

    const ratio = expense.revenueRatio ?? 0;
    const revenueShare = totalMonthlyRevenue * ratio;

    switch (expense.expenseType) {
      case 'fixed':
        result.breakdown.baseAmount = this.applyGrowthRate(monthlyAmount, monthsElapsed, expense.growthRate);
        result.calculatedAmount = result.breakdown.baseAmount;
        break;

      case 'percentage':
        // % of revenue — revenue growth handles scaling; optional growth inflates the ratio, not dollars
        result.breakdown.revenueComponent =
          expense.growthRate && monthsElapsed > 0
            ? totalMonthlyRevenue * this.applyGrowthRate(ratio, monthsElapsed, expense.growthRate)
            : revenueShare;
        result.calculatedAmount = result.breakdown.revenueComponent;
        break;

      case 'hybrid':
        result.breakdown.baseAmount = this.applyGrowthRate(monthlyAmount, monthsElapsed, expense.growthRate);
        result.breakdown.revenueComponent = revenueShare;
        result.calculatedAmount = result.breakdown.baseAmount + result.breakdown.revenueComponent;
        break;

      case 'customer_based': {
        if (customerData) {
          const perCustomer = expense.customerBaseCostPerCustomer ?? 0;
          result.breakdown.customerComponent = this.applyGrowthRate(
            customerData.totalCustomers * perCustomer,
            monthsElapsed,
            expense.growthRate
          );
        }
        result.breakdown.baseAmount = this.applyGrowthRate(
          monthlyBaseAmount,
          monthsElapsed,
          expense.growthRate
        );
        result.calculatedAmount =
          result.breakdown.baseAmount + (result.breakdown.customerComponent ?? 0);
        break;
      }

      default:
        result.breakdown.baseAmount = this.applyGrowthRate(monthlyAmount, monthsElapsed, expense.growthRate);
        result.calculatedAmount = result.breakdown.baseAmount;
    }

    if (monthsElapsed > 0 && expense.growthRate) {
      result.breakdown.growthMultiplier = Math.pow(1 + expense.growthRate, monthsElapsed);
    }

    return result;
  }

  static calculateRevenueAtMonth(
    revenue: StandardizedRevenue,
    targetDate: Date,
    customerData?: StandardizedCustomerData
  ): CalculationResult {
    if (!isActiveInMonth(revenue, targetDate)) {
      return {
        calculatedAmount: 0,
        breakdown: { baseAmount: 0, growthMultiplier: 1 },
      };
    }

    const monthsElapsed = this.calculateMonthsElapsed(revenue.startDate, targetDate);
    const monthlyAmount = this.normalizeToMonthly(revenue.amount, revenue.frequency);

    const result: CalculationResult = {
      calculatedAmount: 0,
      breakdown: { baseAmount: 0, growthMultiplier: 1 },
    };

    switch (revenue.revenueType) {
      case 'customer_driven':
        result.breakdown.baseAmount = customerData?.subscriptionRevenue ?? 0;
        break;
      case 'fixed':
      default:
        result.breakdown.baseAmount = this.applyGrowthRate(
          monthlyAmount,
          monthsElapsed,
          revenue.growthRate
        );
    }

    result.calculatedAmount = result.breakdown.baseAmount;

    if (monthsElapsed > 0 && revenue.growthRate) {
      result.breakdown.growthMultiplier = Math.pow(1 + revenue.growthRate, monthsElapsed);
    }

    return result;
  }

  static calculateTotalMonthlyExpenses(
    expenses: StandardizedExpense[],
    targetDate: Date,
    totalMonthlyRevenue: number,
    customerData?: StandardizedCustomerData
  ): AggregateCalculationResult {
    const itemBreakdowns: CalculationResult[] = [];
    let totalAmount = 0;
    const allWarnings: string[] = [];
    const allErrors: string[] = [];

    for (const expense of expenses) {
      const result = this.calculateExpenseAtMonth(
        expense,
        targetDate,
        totalMonthlyRevenue,
        customerData
      );
      itemBreakdowns.push(result);
      totalAmount += result.calculatedAmount;
      if (result.warnings) allWarnings.push(...result.warnings);
      if (result.errors) allErrors.push(...result.errors);
    }

    return {
      totalAmount,
      itemBreakdowns,
      warnings: allWarnings.length > 0 ? allWarnings : undefined,
      errors: allErrors.length > 0 ? allErrors : undefined,
    };
  }

  static calculateTotalMonthlyRevenues(
    revenues: StandardizedRevenue[],
    targetDate: Date,
    customerData?: StandardizedCustomerData
  ): AggregateCalculationResult {
    const itemBreakdowns: CalculationResult[] = [];
    let totalAmount = 0;
    const allWarnings: string[] = [];
    const allErrors: string[] = [];

    for (const revenue of revenues) {
      const result = this.calculateRevenueAtMonth(revenue, targetDate, customerData);
      itemBreakdowns.push(result);
      totalAmount += result.calculatedAmount;
      if (result.warnings) allWarnings.push(...result.warnings);
      if (result.errors) allErrors.push(...result.errors);
    }

    return {
      totalAmount,
      itemBreakdowns,
      warnings: allWarnings.length > 0 ? allWarnings : undefined,
      errors: allErrors.length > 0 ? allErrors : undefined,
    };
  }

  static standardizeExpenseFromAny(expense: Record<string, unknown>): StandardizedExpense {
    const start = expense.startDate;
    const end = expense.endDate;
    return {
      id: String(expense.id ?? ''),
      name: String(expense.name ?? ''),
      amount: Number(expense.amount ?? expense.monthlyAmount ?? 0),
      expenseType: (expense.expenseType as StandardizedExpense['expenseType']) ?? 'fixed',
      frequency: (expense.frequency as StandardizedExpense['frequency']) ?? 'monthly',
      startDate: start ? new Date(start as string | Date) : undefined,
      endDate: end ? new Date(end as string | Date) : undefined,
      growthRate: expense.growthRate as number | undefined,
      revenueRatio: expense.revenueRatio as number | undefined,
      customerBaseCostPerCustomer: expense.customerBaseCostPerCustomer as number | undefined,
      customerBaseAmount: expense.customerBaseAmount as number | undefined,
    };
  }

  static standardizeRevenueFromAny(revenue: Record<string, unknown>): StandardizedRevenue {
    const start = revenue.startDate;
    const end = revenue.endDate;
    const isCustomerDriven = Boolean(revenue.isCustomerDriven);
    return {
      id: String(revenue.id ?? ''),
      name: String(revenue.name ?? ''),
      amount: Number(revenue.amount ?? revenue.monthlyAmount ?? 0),
      revenueType: isCustomerDriven ? 'customer_driven' : 'fixed',
      frequency: (revenue.frequency as StandardizedRevenue['frequency']) ?? 'monthly',
      startDate: start ? new Date(start as string | Date) : undefined,
      endDate: end ? new Date(end as string | Date) : undefined,
      growthRate: revenue.growthRate as number | undefined,
    };
  }
}
